import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaPlus, FaEdit } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';
const ROWS_PER_PAGE = 34;

const ChaList = () => {
  const navigate = useNavigate();
  const [chaRecords, setChaRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch paginated data from server
  const fetchRecords = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      const res = await fetch(`${API_BASE_URL}/api/masters/cha-get?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setChaRecords(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch CHA error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  // Generate page numbers for tally-style pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">CHA Records</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className="flex items-center gap-1 bg-[#2691cc] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#2691cc] font-medium"
            onClick={() => navigate('/cha/new')}
          >
             <FaPlus />
            Add New
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : chaRecords.length === 0 ? (
        <NoRecordFound message="No CHA records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                
                <th className="px-3 py-2 text-left font-medium">Name</th>
             
                <th className="px-3 py-2 text-left font-medium">Contact Phone</th>
                <th className="px-3 py-2 text-left font-medium">Default Port</th>
                <th className="px-3 py-2 text-left font-medium">Address</th>
                <th className="px-3 py-2 text-left font-medium">Email ID</th>
                <th className="px-3 py-2 text-left font-medium">Bank Name</th>
                <th className="px-3 py-2 text-left font-medium">Account No</th>
                <th className="px-3 py-2 text-left font-medium">VAT/GST No</th>
                <th className="px-3 py-2 text-left font-medium">Aadhar No</th>
                <th className="px-3 py-2 text-left font-medium">License No</th>
                <th className="px-3 py-2 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {chaRecords.map((cha, idx) => (
                <tr key={cha.cha_cma_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  
                  <td className="px-3 py-1 font-semibold whitespace-nowrap">{cha.name}</td>
                
                  <td className="px-3 py-1 whitespace-nowrap">{cha.contact_phone}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{cha.default_port}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{cha.address}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{cha.email_id}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{cha.bank_name}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{cha.account_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{cha.vat_gst_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{cha.aadhar_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{cha.license_no}</td>
                  <td className="px-3 py-1">
                    <button
                      onClick={() => navigate(`/cha/edit/${cha.cha_cma_id}`)}
                      className="text-xs px-2 py-1 bg-[#10a5e9] text-white rounded hover:bg-[#0e8dc6] border border-[#10a5e9] flex items-center gap-1"
                      title="Edit CHA"
                    >
                      <FaEdit />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Pagination Controls */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ChaList;